import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts';
import { Results, TabType } from '../../types/results';
import { downloadResultsPdf } from '../../utils/reportPdf';

interface Props {
  results: Results;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

const ResultsHeader: React.FC<Props> = ({ results, activeTab, onTabChange }) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const correctAnswers = results.answers.filter(a => a.is_correct).length;
  const incorrectAnswers = results.answers.length - correctAnswers;
  const percentage = Math.round((results.scores.raw_score / results.scores.max_score) * 100);

  const TABS: { key: TabType; label: string }[] = [
    { key: 'summary', label: t('results.tabs.summary') },
    { key: 'profile', label: t('results.tabs.profile') },
    { key: 'careers', label: t('results.tabs.careers') },
    { key: 'details', label: t('results.tabs.details') },
  ];
  
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
      {/* Header with title and back button */}
      <div className={`flex justify-between items-center mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <h1 className="text-lg font-bold text-gray-800">
          {t('results.title')} - {results.test_version}
        </h1>
        <div className="flex gap-2">
        <button
          disabled={generating}
          onClick={async () => {
            setGenerating(true);
            try {
              await downloadResultsPdf(results, t, isRTL, i18n.language);
            } catch (e) {
              console.error('[ResultsHeader] PDF generation failed', e);
            } finally {
              setGenerating(false);
            }
          }}
          className="bg-secondary disabled:opacity-60 hover:bg-opacity-90 text-white font-semibold py-1.5 px-4 rounded-lg text-sm transition"
        >
          {generating ? t('common.loading') : t('results.downloadPdf')}
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-primary hover:bg-opacity-90 text-white font-semibold py-1.5 px-4 rounded-lg text-sm transition"
        >
          {t('common.back')}
        </button>
        </div>
      </div>
      
      {/* Main score cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <ScoreCard
          label={t('results.finalScore')}
          value={`${results.scores.raw_score}/${results.scores.max_score}`}
          subtext={`${percentage}%`}
          bgColor="bg-primary bg-opacity-10"
          textColor="text-primary"
        />
        <ScoreCard
          label={t('results.correct')}
          value={correctAnswers.toString()}
          subtext={`/${results.answers.length}`}
          bgColor="bg-green-50"
          textColor="text-green-600"
        />
        <ScoreCard
          label={t('results.incorrect')}
          value={incorrectAnswers.toString()}
          subtext={t('results.answers')}
          bgColor="bg-red-50"
          textColor="text-red-600"
        />
        <ScoreCard
          label={t('results.duration')}
          value={formatDuration(results.total_duration_seconds)}
          subtext={t('results.total')}
          bgColor="bg-secondary bg-opacity-10"
          textColor="text-secondary"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            className={`px-3 py-2 text-sm font-medium whitespace-nowrap transition ${
              activeTab === tab.key
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Sub-component for score cards
interface ScoreCardProps {
  label: string;
  value: string;
  subtext: string;
  bgColor: string;
  textColor: string;
}

const ScoreCard: React.FC<ScoreCardProps> = ({ 
  label, 
  value, 
  subtext, 
  bgColor, 
  textColor 
}) => (
  <div className={`${bgColor} p-3 rounded-lg text-center`}>
    <p className="text-gray-600 text-xs font-medium">{label}</p>
    <p className={`text-xl font-bold ${textColor}`}>{value}</p>
    <p className="text-sm text-gray-500">{subtext}</p>
  </div>
);

export default ResultsHeader;
